// Mirrored in applications/platform-app/components/web-analytics — change both.
import React, { useState } from 'react';
import { useDash } from '../context';
import { useWebQuery } from '../useWebQuery';
import { useTrack } from './shared';
import { ThreatMeter } from './OverviewTab';
import { StackBars } from '../charts/StackBars';
import { BarList } from '../charts/BarList';
import { BusyGem, Button, Card, Empty, ErrorLine, MoreButton, Muted, Note, Tag, Td, Th, severityTone, rowButton } from '../ui';
import { errorMessage } from '../api';
import type { Block, Incident, SecurityResponse } from '../types';
import { BLOCK_KIND_LABEL, INCIDENT_LABEL, RUBY, ago, bucketLabel, countryName, fmt } from '../format';

const IP_LIKE = /^[0-9a-f.:]+(\/\d+)?$/i;
/** An incident whose source can be blocked as an IP / range. */
export const blockableSource = (s: string) => IP_LIKE.test(s);
export const prefillFor = (source: string) => ({ kind: source.includes('/') ? 'net24' as const : 'ip' as const, target: source });

const levelColor = (l: string) => (l === 'High' ? RUBY : l === 'Elevated' ? '#d97706' : '#16a34a');

const untilLabel = (b: Block) => (b.until ? new Date(b.until).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Permanent');

/** Tenant mode: a row the tenant did not create itself is RubyTill's (the API also hides its reason). */
export const lockedForTenant = (b: Block, siteId: string) => b.createdByKind !== 'tenant' || b.scope !== siteId;

export const SecurityTab: React.FC = () => {
  const d = useDash();
  const admin = d.mode === 'admin';
  const sec = useTrack(useWebQuery<SecurityResponse>({
    api: d.api, siteId: d.siteId, range: d.state.range, filters: [], want: null, nonce: d.blocksNonce,
    unfiltered: () => d.api.security(d.siteId, d.state.range),
  }));
  const inc = useTrack(useWebQuery<{ incidents: Incident[] }>({
    api: d.api, siteId: d.siteId, range: d.state.range, filters: [], want: null, nonce: d.blocksNonce,
    unfiltered: () => d.api.incidents(d.siteId, d.state.range),
  }));
  const blk = useTrack(useWebQuery<{ blocks: Block[] }>({
    api: d.api, siteId: d.siteId, range: { range: '30d', compare: false }, filters: [], want: null, nonce: d.blocksNonce,
    unfiltered: () => d.api.blocks(d.siteId),
  }));
  const [busy, setBusy] = useState<string | null>(null);

  const s = sec.data;
  const incidents = inc.data?.incidents || [];
  const blocks = blk.data?.blocks || [];
  const all = d.siteId === '_all';
  const siteName = (id: string) => d.sites.find((x) => x.siteId === id)?.name || id;

  const unblock = async (b: Block) => {
    setBusy(b.blockId);
    try {
      await d.api.deleteBlock(d.siteId, b.blockId, all ? b.scope : undefined);
      d.toast(`Unblocked ${b.target}. Recorded in the audit log.`);
      d.bumpBlocks();
    } catch (e) {
      d.toast(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      {d.filtered && <div className="text-[11px] text-stone-500 mb-2">Security data covers all traffic — filters don't apply to this tab.</div>}
      {sec.error && <ErrorLine message={sec.error} />}
      <div className="grid gap-3.5 grid-cols-1 sm:grid-cols-2 xl:grid-cols-6">
        <Card className="sm:col-span-2">
          <div className="text-[11px] text-stone-500">Threat level</div>
          <div className="flex items-center gap-3.5 mt-2">
            <b className="text-xl" style={{ color: levelColor(s?.level || 'Low') }} data-testid="wa-threat-level">{s?.level || '—'}</b>
            <ThreatMeter score={s?.score ?? 0} />
          </div>
          <Note>Combines blocked traffic, failed logins, card testing and probing, compared with this site's normal level.</Note>
        </Card>
        {[
          ['Blocked requests', fmt(s?.blocked ?? 0), 'firewall + rate limits'],
          ['Bot traffic', `${s?.botPct ?? 0}%`, 'removed from your stats'],
          ['Failed logins', fmt(s?.failedLogins ?? 0), s ? `${s.logins.failRate}% of login attempts` : ''],
          ['Active blocks', String(s?.activeBlocks ?? blocks.length), admin ? 'all scopes' : 'on your site + platform'],
        ].map(([l, v, sub]) => (
          <Card key={l}>
            <div className="text-[11px] text-stone-500 font-medium">{l}</div>
            <div className="text-[22px] font-bold mt-1">{v}</div>
            <div className="text-[11px] mt-1 text-stone-500">{sub}</div>
          </Card>
        ))}
      </div>

      <Card title="Requests: allowed vs blocked" className="mt-3.5">
        <div className="flex gap-3.5 text-[11px] text-stone-500 mb-1.5">
          <span><i className="inline-block w-2.5 h-2 rounded-sm mr-1.5 align-middle bg-stone-200" />Allowed</span>
          <span><i className="inline-block w-2.5 h-2 rounded-sm mr-1.5 align-middle" style={{ background: RUBY }} />Blocked</span>
        </div>
        {s && s.series.some((p) => p.allowed || p.blocked)
          ? <StackBars bottom={s.series.map((p) => p.allowed)} top={s.series.map((p) => p.blocked)} labels={s.series.map((p) => bucketLabel(p.label))} />
          : <Empty />}
      </Card>

      <Card title={<>Suspicious activity <Muted>· click a row for details</Muted></>} className="mt-3.5" testId="wa-incidents">
        {inc.error ? <ErrorLine message={inc.error} /> : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead><tr><Th>When</Th>{all && <Th>Website</Th>}<Th>What</Th><Th>Severity</Th><Th>Source</Th><Th>Country</Th><Th>Network</Th><Th num>Requests</Th><Th>Status</Th><Th /></tr></thead>
              <tbody>
                {incidents.length === 0 && <tr><Td colSpan={all ? 10 : 9} className="text-stone-500">Nothing suspicious in this period.</Td></tr>}
                {incidents.map((i) => (
                  <tr key={i.incidentId} {...rowButton(() => d.openIncident(i))} data-testid={`wa-incident-${i.incidentId}`}>
                    <Td>{ago(i.lastAt)}</Td>
                    {all && <Td>{i.siteName || siteName(i.siteId)}</Td>}
                    <Td><b>{INCIDENT_LABEL[i.type] || i.type}</b></Td>
                    <Td><Tag tone={severityTone(i.severity)}>{i.severity}</Tag></Td>
                    <Td className="font-mono text-xs">{i.source}</Td>
                    <Td>{i.country ? countryName(i.country) : '—'}</Td>
                    <Td>{i.network || '—'}</Td>
                    <Td num>{fmt(i.hits)}</Td>
                    <Td>{i.status === 'blocked' ? <Tag tone="green">Blocked</Tag> : i.status === 'resolved' ? <Tag tone="gray">Resolved</Tag> : <Tag tone="amber">Watching</Tag>}</Td>
                    <Td>
                      {i.status === 'watching' && blockableSource(i.source) && (
                        <Button tone="ruby" size="sm" onClick={(e) => { e.stopPropagation(); d.openBlock(prefillFor(i.source)); }}>Block</Button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid gap-3.5 mt-3.5 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        <Card title="Login activity">
          <BarList rows={s ? [
            { key: 'ok', label: 'Successful logins', value: s.logins.ok },
            { key: 'failed', label: 'Failed logins', value: s.logins.failed },
            ...(s.declines ? [{ key: 'declines', label: 'Declined card payments', value: s.declines }] : []),
          ].filter((r) => r.value > 0) : []} />
          <Note>{d.siteKind === 'company' ? 'Merchant back-office logins.' : 'Customer account logins on this storefront.'} Email addresses are never shown.</Note>
        </Card>
        <Card title="Bots"><BarList rows={(s?.bots || []).map((b) => ({ key: b.name, label: b.name, value: b.count }))} /></Card>
        <Card title="Most targeted paths"><BarList rows={(s?.paths || []).map((p) => ({ key: p.path, label: p.path, value: p.count }))} /></Card>
      </div>

      <Card title="Blocklist" className="mt-3.5" action={<MoreButton onClick={() => d.openBlock()}>+ Add block</MoreButton>} testId="wa-blocklist">
        {blk.error ? <ErrorLine message={blk.error} /> : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead><tr><Th>Blocked</Th><Th>Type</Th><Th>Applies to</Th><Th>Until</Th><Th>Added by</Th><Th>Reason</Th><Th /></tr></thead>
              <tbody>
                {blocks.length === 0 && <tr><Td colSpan={7} className="text-stone-500">No active blocks.</Td></tr>}
                {blocks.map((b) => {
                  const locked = !admin && lockedForTenant(b, d.siteId);
                  return (
                    <tr key={b.blockId} data-testid={`wa-block-${b.blockId}`}>
                      <Td className="font-mono text-xs">{b.target}</Td>
                      <Td>{BLOCK_KIND_LABEL[b.kind] || b.kind}</Td>
                      <Td>{b.scope === 'all' ? <Tag tone="red">All websites</Tag> : <Tag tone="gray">{siteName(b.scope)}</Tag>}</Td>
                      <Td>{untilLabel(b)}</Td>
                      <Td>{b.createdBy}</Td>
                      <Td className="text-stone-500 max-w-[240px] truncate">{b.reason || '—'}</Td>
                      <Td>
                        {locked ? <span className="text-stone-500 text-[11px]">Set by RubyTill</span> : (
                          <Button size="sm" disabled={busy === b.blockId} onClick={() => unblock(b)}>{busy === b.blockId ? <BusyGem /> : 'Unblock'}</Button>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Note>{admin
          ? 'Super Admin can block on one website or on every website.'
          : 'Your blocks apply to your website only. Blocks marked "Set by RubyTill" protect every RubyTill website and can\'t be removed here.'}</Note>
      </Card>
    </div>
  );
};

export default SecurityTab;
